import React, { useEffect, useMemo, useState } from 'react'
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  useMapEvents,
  useMap,
} from 'react-leaflet'
import L, { LatLngExpression, LeafletMouseEvent } from 'leaflet'
import { supabase } from '../../lib/supabase'
import {
  fetchGeoJsonWithEncoding,
  fixMojibakeUtf8AsLatin1,
  normalizeGeoJsonTextProperties,
} from '../../lib/geojson-encoding'
import './fixLeafletIcons'
import './visor-bmp-popup.css'
import LegendControl from './LegendControl'

import GeoJsonUrl from '../../assets/layers/bmp_municipios.geojson?url'

// Todas las capas GeoJSON de BMP: solo URLs (evita que Vite interprete .geojson como JS)
const bmpLayerUrlModules = import.meta.glob<string>(
  '../../assets/layers/bmp/*.geojson',
  { eager: true, query: '?url', import: 'default' }
)
const BMP_LIMITE_LAYER_ID = 'BMP_unido6'

/** Título legible sin la palabra BMP en el nombre (excepto «Límite BMP») */
function layerDisplayName(fileName: string): string {
  if (fileName === BMP_LIMITE_LAYER_ID) return 'Límite BMP'
  let s = fileName.replace(/_/g, ' ')
  s = s.replace(/\bBMP\b/gi, ' ').replace(/\s+/g, ' ').trim()
  return s || fileName.replace(/_/g, ' ')
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Campos popup: clave GeoJSON → etiqueta visible */
type BmpPopupField = { key: string; label: string }

const BMP_LAYER_POPUP_FIELDS: Record<string, BmpPopupField[]> = {
  Aguas_residuales_BMP: [
    { key: 'descripcio', label: 'Descripción' },
    { key: 'nombreCent', label: 'Nombre centro' },
    { key: 'NOMBRE', label: 'Municipio' },
  ],
  Aparcamientos_espacios_naturales_BMP: [
    { key: 'equip_b_no', label: 'Equipamiento' },
    { key: 'NOMBRE', label: 'Nombre' },
  ],
  Bienes_interes_cultural_BMP: [
    { key: 'NOMBRE', label: 'Nombre' },
    { key: 'd_bien_den', label: 'Denominación' },
    { key: 'l_url_pweb', label: 'URL' },
  ],
  Centros_educativos_BMP: [
    { key: 'n_centro', label: 'Nombre' },
    { key: 'n_tip_cent', label: 'Tipo de centro' },
    { key: 'n_mun', label: 'Municipio' },
    { key: 'v_num_telf', label: 'Tlf' },
    { key: 'l_web', label: 'Web' },
  ],
  Contaminacion_agraria_factor_agroambiental_ganadero_BMP: [
    { key: 'n_comar_ag', label: 'Comarca' },
    { key: 'n_mun', label: 'Municipio' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
    { key: 'fa_mun2017', label: 'fa_mun2017' },
    { key: 'fa_mun2018', label: 'fa_mun2018' },
    { key: 'fa_mun2019', label: 'fa_mun2019' },
    { key: 'fa_mun2020', label: 'fa_mun2020' },
    { key: 'fa_mun2021', label: 'fa_mun2021' },
    { key: 'fa_mun2022', label: 'fa_mun2022' },
    { key: 'fa_mun2023', label: 'fa_mun2023' },
    { key: 'fa_mun2024', label: 'fa_mun2024' },
  ],
  Contaminacion_agraria_indice_carga_ganadera_BMP: [
    { key: 'n_comar_ag', label: 'Comarca' },
    { key: 'n_mun', label: 'Municipio' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
    { key: 'fa_m17_prm', label: 'fa_m17_prm' },
    { key: 'fa_m18_prm', label: 'fa_m18_prm' },
    { key: 'fa_m19_prm', label: 'fa_m19_prm' },
    { key: 'fa_m20_prm', label: 'fa_m20_prm' },
    { key: 'fa_m21_prm', label: 'fa_m21_prm' },
    { key: 'fa_m22_prm', label: 'fa_m22_prm' },
    { key: 'fa_m23_prm', label: 'fa_m23_prm' },
    { key: 'fa_m24_prm', label: 'fa_m24_prm' },
  ],
  Contaminacion_agraria_nitratos_aguas_subterraneas_BMP: [
    { key: 'n_comar_ag', label: 'Comarca' },
    { key: 'n_mun', label: 'Municipio' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
    { key: 'v_nitr2006', label: 'v_nitr2006' },
    { key: 'v_nitr2007', label: 'v_nitr2007' },
    { key: 'v_nitr2008', label: 'v_nitr2008' },
    { key: 'v_nitr2009', label: 'v_nitr2009' },
    { key: 'v_nitr2010', label: 'v_nitr2010' },
    { key: 'v_nitr2011', label: 'v_nitr2011' },
    { key: 'v_nitr2012', label: 'v_nitr2012' },
    { key: 'v_nitr2013', label: 'v_nitr2013' },
    { key: 'v_nitr2014', label: 'v_nitr2014' },
    { key: 'v_nitr2015', label: 'v_nitr2015' },
    { key: 'v_nitr2016', label: 'v_nitr2016' },
    { key: 'v_nitr2017', label: 'v_nitr2017' },
    { key: 'v_nitr2018', label: 'v_nitr2018' },
    { key: 'v_nitr2019', label: 'v_nitr2019' },
    { key: 'v_nitr2020', label: 'v_nitr2020' },
    { key: 'v_nitr2021', label: 'v_nitr2021' },
  ],
  Zonas_humedas_interes_especial_BMP: [
    { key: 'n_zhie', label: 'Nombre' },
    { key: 'n_figura', label: 'Figura' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
  ],
  flora_cyl_arboles_notables_BMP: [
    { key: 'especie', label: 'Especie' },
    { key: 't_municipa', label: 'Municipio' },
    { key: 'paraje', label: 'Paraje' },
    { key: 'nombre', label: 'Nombre' },
  ],
  flora_cyl_material_forestal_reproduccion_BMP: [
    { key: 'especie', label: 'Especie' },
    { key: 'reg_proced', label: 'Región procedencia' },
    { key: 'nomb_monte', label: 'Nombre monte' },
    { key: 'tm', label: 'Municipio' },
  ],
  montes_cyl_certificacion_forestal_sostenible_BMP: [
    { key: 'grupo_ord', label: 'Grupo ordenación' },
    { key: 'sp_princip', label: 'Especie principal' },
    { key: 'certific', label: 'Certificación' },
    { key: 'd_nombre', label: 'Nombre' },
    { key: 'tipo_prop', label: 'Tipo propiedad' },
  ],
  /** Nombre + Nombre_2 en datos comparten atributo nombre_2 (espacio / macizo) */
  red_espacios_naturales_BMP: [
    { key: 'NOMBRE', label: 'Municipio' },
    { key: 'nombre_2', label: 'Nombre' },
    { key: 'nombre_2', label: 'Nombre_2' },
    { key: 'figura', label: 'Figura' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
  ],
  red_natura_2000_zepa_BMP: [
    { key: 'NOMBRE', label: 'Municipio' },
    { key: 'SITE_NAME', label: 'Nombre' },
    { key: 'FIGURA', label: 'Figura' },
    { key: 'TIPO_ESPAC', label: 'Tipo espacio' },
    { key: 'sup_ha', label: 'Superficie (ha)' },
  ],
  residuos_instalaciones_tratamiento_BMP: [
    { key: 'nombreMuni', label: 'Municipio' },
    { key: 'descripcio', label: 'Descripción' },
    { key: 'urlDetall0', label: 'URL' },
    { key: 'nombreEnti', label: 'Entidad' },
  ],
  sanidad_centros_de_salud_BMP: [
    { key: 'd_tipocent', label: 'Tipo centro' },
    { key: 'a_direccio', label: 'Dirección' },
    { key: 'NOMBRE', label: 'Nombre' },
  ],
  servicios_instalaciones_BMP: [
    { key: 'NOMBRE', label: 'Municipio' },
    { key: 'TOPONIMO', label: 'Topónimo' },
    { key: 'TIPO', label: 'Tipo' },
    { key: 'FENOMENO', label: 'Fenómeno' },
  ],
  vivienda_cyl_programa_rehabitare_BMP: [
    { key: 'NOMBRE', label: 'Nombre' },
    { key: 'n_instituc', label: 'Institución' },
    { key: 'url_doc_in', label: 'URL' },
  ],
}

const BMP_POPUP_WRAP_STYLE =
  'width:100%;max-width:100%;min-height:0;max-height:min(65vh,480px);overflow:auto;overflow-x:hidden;overflow-wrap:break-word;word-break:normal;padding-right:4px;'

function formatPopupValue(raw: string, fieldKey: string): string {
  const t = fixMojibakeUtf8AsLatin1(raw).trim()
  if (!t) return '—'
  const isUrlField =
    /url|_web$/i.test(fieldKey) ||
    fieldKey === 'l_url_pweb' ||
    fieldKey === 'urlDetall0' ||
    fieldKey === 'url_doc_in'
  if (isUrlField && /^https?:\/\//i.test(t)) {
    const esc = escapeHtml(t)
    return `<a href="${esc}" target="_blank" rel="noopener noreferrer" style="word-break:break-all">${esc}</a>`
  }
  return escapeHtml(t)
}

function getPopupRowsForBmpLayer(
  layerId: string,
  props: Record<string, unknown>
): { label: string; key: string; value: string }[] {
  const fields = BMP_LAYER_POPUP_FIELDS[layerId]
  if (!fields?.length) {
    return Object.entries(props).map(([key, v]) => ({
      key,
      label: key,
      value: formatPopupValue(v != null ? String(v) : '', key),
    }))
  }
  const rows: { label: string; key: string; value: string }[] = []
  for (const { key, label } of fields) {
    if (!Object.prototype.hasOwnProperty.call(props, key)) continue
    const v = props[key]
    rows.push({
      label,
      key,
      value: formatPopupValue(v != null ? String(v) : '', key),
    })
  }
  return rows
}

const bmpLayersListAll = Object.entries(bmpLayerUrlModules)
  .map(([path, mod]) => {
    const name = path.split('/').pop()?.replace(/\.geojson$/i, '') ?? path
    const url = typeof mod === 'string' ? mod : (mod as any)?.default ?? ''
    return { id: name, name, displayName: layerDisplayName(name), url }
  })
  .filter((l) => l.url)

const bmpLimiteLayer = bmpLayersListAll.find((l) => l.id === BMP_LIMITE_LAYER_ID) ?? null
const bmpLayersOther = bmpLayersListAll
  .filter((l) => l.id !== BMP_LIMITE_LAYER_ID)
  .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es', { sensitivity: 'base' }))
/** Todas las capas BMP (para estado / carga); el sidebar y el mapa usan orden propio */
const bmpLayersList = bmpLayersListAll

// ---------------- ICONOS LEAFLET ----------------
const defaultIconRetinaUrl =
  'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png'
const defaultIconUrl =
  'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png'
const defaultShadowUrl =
  'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: defaultIconRetinaUrl,
  iconUrl: defaultIconUrl,
  shadowUrl: defaultShadowUrl,
})

// ---------------- TYPES ----------------
export interface MarkerData {
  id: string | number
  position: [number, number]
  title?: string
  description?: string
}



export interface MapLeafletProps {
  center?: LatLngExpression
  zoom?: number
  height?: string
  markers?: MarkerData[]
  sidebarVisible?: boolean
  onMapClick?: (
    latlng: { lat: number; lng: number },
    e: LeafletMouseEvent
  ) => void
  onMarkerClick?: (marker: MarkerData) => void
  className?: string
  children?: React.ReactNode
}

// ---------------- HELPERS ----------------
function MapEvents({
  onMapClick,
}: {
  onMapClick?: (
    latlng: { lat: number; lng: number },
    e: LeafletMouseEvent
  ) => void
}) {
  useMapEvents({
    click: (e) => {
      onMapClick?.({ lat: e.latlng.lat, lng: e.latlng.lng }, e)
    },
  })
  return null
}

function FitGeoJSON({ data }: { data: any }) {
  const map = useMap()

  useEffect(() => {
    if (!data) return
    const layer = L.geoJSON(data)
    const bounds = layer.getBounds()
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] })
    }
  }, [data, map])

  return null
}


/** Filtra un FeatureCollection por propiedad CODMUN = codigo_ine del municipio seleccionado */
function filterGeoJSONByMunicipio(geojson: any, codigoIne: string | null): any {
  if (!geojson || !codigoIne) return geojson
  const features = geojson.features?.filter(
    (f: any) => String(f.properties?.CODMUN ?? '') === String(codigoIne)
  )
  if (!features?.length) return null
  return { type: 'FeatureCollection', features }
}

function buildBmpPopupHtml(layerTitle: string, rows: { label: string; value: string }[]): string {
  const title = escapeHtml(layerTitle)
  if (!rows.length) {
    return `<div class="bmp-popup-scroll" style="${BMP_POPUP_WRAP_STYLE}"><strong class="bmp-popup-title">${title}</strong><br/>Sin datos</div>`
  }
  const body = rows
    .map(
      (r) =>
        `<div class="bmp-popup-row" style="margin-bottom:6px;line-height:1.35"><strong>${escapeHtml(r.label)}:</strong> <span class="bmp-popup-val">${r.value}</span></div>`
    )
    .join('')
  return `<div class="bmp-popup-scroll" style="${BMP_POPUP_WRAP_STYLE}"><strong class="bmp-popup-title" style="display:block;margin-bottom:8px">${title}</strong>${body}</div>`
}

// ---------------- COMPONENTE ----------------
export default function Visor({
  center = [41.65, -4.72],
  zoom = 6,
  height = '70vh',
  markers = [],
  sidebarVisible = false,
  onMapClick,
  onMarkerClick,
  className = '',
  children,
}: MapLeafletProps) {
  // ---------- ESTADOS ----------

  const [geojsonLocal, setGeojsonLocal] = useState<any>(null)
  // Visibilidad de capas BMP (por id/nombre)
  const [visibleBmpLayers, setVisibleBmpLayers] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    bmpLayersList.forEach((l) => { init[l.id] = false })
    return init
  })
  // GeoJSON cargados por capa BMP (se cargan al activar la capa vía fetch)
  const [bmpGeojsons, setBmpGeojsons] = useState<Record<string, any>>({})
  // Municipios para el filtro (tabla municipios: codigo_ine, nombre)
  const [municipios, setMunicipios] = useState<Array<{ codigo_ine: string; nombre: string }>>([])
  const [selectedMunicipio, setSelectedMunicipio] = useState<string | null>(null)
  const [loadingMunicipios, setLoadingMunicipios] = useState(false)

  const [visibleLayers, setVisibleLayers] = useState({
    local: true,
    ckan: true,
    markers: true,
  })

  // ---------- CARGA GEOJSON LOCAL ----------
  useEffect(() => {
    const loadLocal = async () => {
      const data = await fetchGeoJsonWithEncoding(GeoJsonUrl)
      delete data.bbox
      setGeojsonLocal(normalizeGeoJsonTextProperties(data))
    }
    loadLocal()
  }, [])

  // ---------- CARGA MUNICIPIOS (para filtro) ----------
  useEffect(() => {
    if (!sidebarVisible) return
    const loadMunicipios = async () => {
      try {
        setLoadingMunicipios(true)
        const { data, error } = await supabase
          .from('municipios')
          .select('codigo_ine, nombre')
          .order('nombre', { ascending: true })
        if (error) throw error
        setMunicipios((data as Array<{ codigo_ine: string; nombre: string }>) ?? [])
      } catch {
        setMunicipios([])
      } finally {
        setLoadingMunicipios(false)
      }
    }
    loadMunicipios()
  }, [sidebarVisible])

  

  // ---------- MARKERS ----------
  const markerElements = useMemo(
    () =>
      markers.map((m) => (
        <Marker
          key={m.id}
          position={m.position}
          eventHandlers={{
            click: () => onMarkerClick?.(m),
          }}
        >
          {(m.title || m.description) && (
            <Popup>
              {m.title && <strong>{m.title}</strong>}
              {m.description && <div>{m.description}</div>}
            </Popup>
          )}
        </Marker>
      )),
    [markers, onMarkerClick]
  )

  // ---------- RENDER ----------
  return (
    <div className={`flex w-full ${className}`} style={{ height }}>
      {/* SIDEBAR */}
      {sidebarVisible && (
        <div className="w-64 min-w-0 bg-white shadow-lg z-[1000] p-4 overflow-y-auto overflow-x-hidden">
          <h3 className="font-bold mb-4">Capas</h3>

          {/* LOCAL */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={visibleLayers.local}
              onChange={() =>
                setVisibleLayers((v) => ({ ...v, local: !v.local }))
              }
            />
            <span>Municipios</span>
          </label>

          {bmpLimiteLayer && (
            <label className="flex items-start gap-2 mb-2 min-w-0">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0"
                checked={!!visibleBmpLayers[bmpLimiteLayer.id]}
                onChange={async () => {
                  const next = !visibleBmpLayers[bmpLimiteLayer.id]
                  setVisibleBmpLayers((prev) => ({ ...prev, [bmpLimiteLayer.id]: next }))
                  if (next && !bmpGeojsons[bmpLimiteLayer.id]) {
                    try {
                      const data = await fetchGeoJsonWithEncoding(bmpLimiteLayer.url)
                      setBmpGeojsons((prev) => ({
                        ...prev,
                        [bmpLimiteLayer.id]: normalizeGeoJsonTextProperties(data),
                      }))
                    } catch {
                      setBmpGeojsons((prev) => ({ ...prev, [bmpLimiteLayer.id]: null }))
                    }
                  }
                }}
              />
              <span className="text-sm break-words min-w-0">{bmpLimiteLayer.displayName}</span>
            </label>
          )}

          {/* Filtro por municipio */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Municipio
            </label>
            <select
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={selectedMunicipio ?? ''}
              onChange={(e) =>
                setSelectedMunicipio(e.target.value ? e.target.value : null)
              }
              disabled={loadingMunicipios}
            >
              <option value="">Todos</option>
              {municipios.map((m) => (
                <option key={m.codigo_ine} value={m.codigo_ine}>
                  {m.nombre}
                </option>
              ))}
            </select>
            {loadingMunicipios && (
              <p className="text-xs text-gray-500 mt-1">Cargando municipios…</p>
            )}
          </div>

          {/* Resto de capas BMP */}
          {bmpLayersOther.map((layer) => (
            <label key={layer.id} className="flex items-start gap-2 mb-2 min-w-0">
              <input type="checkbox" className="mt-0.5 shrink-0" checked={!!visibleBmpLayers[layer.id]} onChange={async () => {
                  const next = !visibleBmpLayers[layer.id]
                  setVisibleBmpLayers((prev) => ({ ...prev, [layer.id]: next }))
                  if (next && !bmpGeojsons[layer.id]) {
                    try {
                      const data = await fetchGeoJsonWithEncoding(layer.url)
                      setBmpGeojsons((prev) => ({ ...prev, [layer.id]: normalizeGeoJsonTextProperties(data) }))
                    } catch {
                      setBmpGeojsons((prev) => ({ ...prev, [layer.id]: null }))
                    }
                  }
                }} />
              <span className="text-sm break-words min-w-0">{layer.displayName}</span>
            </label>
          ))}

          {/* MARKERS */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={visibleLayers.markers}
              onChange={() =>
                setVisibleLayers((v) => ({ ...v, markers: !v.markers }))
              }
            />
            <span>Marcadores</span>
          </label>

          {children}
        </div>
      )}

      {/* MAPA */}
      <div className="flex-1">
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          <LegendControl />
          <MapEvents onMapClick={onMapClick} />

          {/* Límite BMP: debajo de municipios en el mapa; en el panel, justo bajo «Municipios» */}
          {bmpLimiteLayer &&
            visibleBmpLayers[bmpLimiteLayer.id] &&
            bmpGeojsons[bmpLimiteLayer.id] &&
            (() => {
              const geojson = bmpGeojsons[bmpLimiteLayer.id]
              const dataFiltered = filterGeoJSONByMunicipio(geojson, selectedMunicipio)
              if (!dataFiltered) return null
              const layer = bmpLimiteLayer
              return (
                <React.Fragment key={layer.id}>
                  <GeoJSON
                    key={`limite-${selectedMunicipio ?? 'all'}`}
                    data={dataFiltered}
                    interactive={false}
                    style={{
                      color: '#b45309',
                      weight: 2.5,
                      fillOpacity: 0.06,
                    }}
                  />
                  <FitGeoJSON data={dataFiltered} />
                </React.Fragment>
              )
            })()}

          {/* GEOJSON LOCAL — municipios encima del límite BMP */}
          {geojsonLocal && visibleLayers.local && (
            <>
              <GeoJSON
                data={geojsonLocal}
                style={{
                  color: '#964594',
                  weight: 2,
                  fillOpacity: 0,
                }}
                 onEachFeature={(feature, layer) => {
              layer.on({
                click: () => {
                  const props = feature.properties || {}
                  layer
                    .bindPopup(`
                      <strong>${props.NOMBRE || "Elemento"}</strong><br/>
                      ${props.AREA ? `<strong>Área:</strong> ${props.AREA}` : ''}
                    `)
                    .openPopup()
                },
              })
            }}
              />
              <FitGeoJSON data={geojsonLocal} />
            </>
          )}

          {/* GEOJSON BMP (filtrado por municipio si hay selección) */}
          {bmpLayersOther.map((layer) => {
            const visible = visibleBmpLayers[layer.id]
            const geojson = bmpGeojsons[layer.id]
            if (!geojson || !visible) return null
            const dataFiltered = filterGeoJSONByMunicipio(
              geojson,
              selectedMunicipio
            )
            if (!dataFiltered) return null
            return (
              <React.Fragment key={layer.id}>
                <GeoJSON
                  key={selectedMunicipio ?? 'all'}
                  data={dataFiltered}
                  style={{
                    color: '#3388ff',
                    weight: 1.5,
                    fillOpacity: 0.2,
                  }}
                  onEachFeature={(feature, leafletLayer) => {
                    leafletLayer.on({
                      click: () => {
                        const props = feature.properties || {}
                        const rows = getPopupRowsForBmpLayer(layer.id, props).map(({ label, value }) => ({
                          label,
                          value,
                        }))
                        const content = buildBmpPopupHtml(layer.displayName, rows)
                        leafletLayer.bindPopup(content).openPopup()
                      },
                    })
                  }}
                />
                <FitGeoJSON data={dataFiltered} />
              </React.Fragment>
            )
          })}



          {visibleLayers.markers && markerElements}
        </MapContainer>
      </div>
    </div>
  )
}
